import React, { useCallback, useEffect, useState } from "react";
import { Table, Dropdown, Menu, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../Layout/mainLayout";
import {
    viewAgentMarginDetailsList,
    addNewAgentMarginDetails,
    updateAgentMarginDetails,
    viewAgentMarginDetails,
} from "../../Redux/Actions/agentMarginDetailsActions";
import { EllipsisOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import ListTopSearch from "../../Global Functions/listTopSearch";
import toast from "react-hot-toast";
import AgentMarginDetailsPopup from "./agentMarginDetailsPopup";
import "./listAgentMarginDetails.css";

const ListAgentMarginDetails = () => {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [popupLoading, setPopupLoading] = useState(false);
    const [popupVisible, setPopupVisible] = useState(false);  // ← added

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { agentMarginDetailsList, loading } = useSelector((state) => state.agentMarginDetails || {});

    const fetchDetails = useCallback(async (page, pageSize) => {
        const payload = { page, limit: pageSize, filter: debouncedSearch };
        await dispatch(viewAgentMarginDetailsList(payload));
    }, [dispatch, debouncedSearch]);

    useEffect(() => {
        fetchDetails(pagination.current, pagination.pageSize);
    }, [fetchDetails, pagination.current, pagination.pageSize]);

    useEffect(() => {
        if (agentMarginDetailsList?.total) {
            setPagination((prev) => ({ ...prev, total: agentMarginDetailsList.total }));
        }
    }, [agentMarginDetailsList]);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const handleTableChange = (pagination) => setPagination(pagination);

    const handleAdd = () => {
        setSelectedRecord(null);
        setPopupVisible(true);  // ← open popup for add
    };

    const handleEdit = async (record) => {
        try {
            setPopupLoading(true);
            const res = await dispatch(viewAgentMarginDetails(record._id));
            if (res?.statusCode === 200) {
                setSelectedRecord(res.data);
                setPopupVisible(true);  // ← open popup for edit
            }
        } catch {
            toast.error("Failed to load details");
        } finally {
            setPopupLoading(false);
        }
    };

    const handlePopupClose = () => {
        setPopupVisible(false);
        setSelectedRecord(null);
    };

    const handleSave = async (payload) => {
        try {
            let res;
            if (selectedRecord?._id) {
                res = await dispatch(updateAgentMarginDetails(selectedRecord._id, payload));
            } else {
                res = await dispatch(addNewAgentMarginDetails(payload));
            }
            if (res?.statusCode === 200 || res?.statusCode === 201) {
                // toast.success(selectedRecord?._id ? "Updated successfully" : "Added successfully");
                handlePopupClose();
                fetchDetails(pagination.current, pagination.pageSize);
            } else {
                toast.error(res?.message || "Something went wrong");
            }
        } catch {
            toast.error("Something went wrong");
        }
    };

    const columns = [
        {
            title: "Sr No.",
            align: "center",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Role",
            dataIndex: "role",
            align: "center",
            render: (val) => (
                <span style={{
                    display: "inline-flex", alignItems: "center", width: "auto",
                    padding: "4px 12px", borderRadius: "20px",
                    fontSize: "12px", fontWeight: 500,
                    background: "#eff6ff", color: "#1d4ed8",
                    border: "1px solid #bfdbfe", whiteSpace: "nowrap",
                }}>
                    {val || "—"}
                </span>
            ),
        },
        {
            title: "Lead From",
            dataIndex: "lead_from",
            align: "center",
            render: (val) => val ? val.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—",
        },
        {
            title: "Agent Margin",
            dataIndex: "agent_margin",
            align: "center",
            render: (val) => val !== undefined ? `${val}%` : "—",
        },
        {
            title: "Admin Margin",
            dataIndex: "admin_margin",
            align: "center",
            render: (val) => val !== undefined ? `${val}%` : "—",
        },
        {
            title: "Other Margin",
            dataIndex: "other_margin",
            align: "center",
            render: (val) => val !== undefined ? `${val}%` : "—",
        },
        {
            title: "Note",
            dataIndex: "note",
            align: "center",
            render: (val) => val || "—",
        },
        // {
        //     title: "Created At",
        //     dataIndex: "created_at",
        //     align: "center",
        //     render: (date) => date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
        // },
        {
            title: "Action",
            align: "center",
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="view">
                                <Link to={`/agent_margin_details/view/${record._id}`}>View</Link>
                            </Menu.Item>
                            {canEdit && (
                                <Menu.Item key="edit" onClick={() => handleEdit(record)}>
                                    Edit
                                </Menu.Item>
                            )}
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button shape="square" icon={<EllipsisOutlined />} loading={popupLoading} />
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            <div style={{ position: "absolute", marginTop: "10.55%", marginLeft: "87.2%" }}>
                {canAdd && (
                    <Button
                        style={{ background: "#070C18", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={handleAdd}
                    >
                        Add Margin Details
                    </Button>
                )}
            </div>
            <MainLayout>
                <div className="amd-container">
                    <div className="amd-table-wrap">
                        <ListTopSearch
                            h3heading="Agent Margin Details"
                            setState={setSearchQuery}
                        />
                        <div style={{ overflowX: "auto", padding: "0px 60px" }}>
                            <Table
                                dataSource={Array.isArray(agentMarginDetailsList?.data) ? agentMarginDetailsList.data : []}
                                pagination={pagination}
                                scroll={{ x: 1200, y: 500 }}
                                onChange={handleTableChange}
                                loading={loading}
                                columns={columns}
                                rowKey="_id"
                                rowClassName={() => "text-center"}
                            />
                        </div>
                    </div>
                </div>
            </MainLayout>

            <AgentMarginDetailsPopup
                visible={popupVisible}
                onClose={handlePopupClose}
                existingData={selectedRecord}
                onSave={handleSave}
            />
        </>
    );
};

export default ListAgentMarginDetails;